import api from './api';

// Notification API service for comprehensive notification management
export const notificationApi = {
  // ==================== USER NOTIFICATION ENDPOINTS ====================
  
  /**
   * Get user notifications with advanced filtering and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of notifications to fetch (default: 20)
   * @param {number} params.offset - Offset for pagination (default: 0)
   * @param {boolean} params.unread - Filter for unread notifications only
   * @param {string} params.type - Filter by specific notification type
   * @param {string} params.category - Filter by category (club, program, event, etc.)
   * @param {string} params.priority - Filter by priority (low, medium, high, urgent)
   * @returns {Promise} API response with notifications and pagination
   */
  getNotifications: (params = {}) => {
    const queryString = new URLSearchParams({
      limit: '20',
      offset: '0',
      ...params
    }).toString();
    return api.get(`/api/notifications?${queryString}`);
  },

  /**
   * Get unread notification count
   * @param {string} category - Optional category filter
   * @returns {Promise} API response with unread count
   */
  getUnreadCount: (category) => {
    const params = category ? `?category=${category}` : '';
    return api.get(`/api/notifications/unread-count${params}`);
  },

  /**
   * Mark single notification as read
   * @param {string} notificationId - Notification ID to mark as read
   * @returns {Promise} API response
   */
  markAsRead: (notificationId) => {
    return api.put(`/api/notifications/${notificationId}/read`);
  },

  /**
   * Mark all notifications as read
   * @param {string} type - Optional: specific notification type to mark as read
   * @returns {Promise} API response with count of marked notifications
   */
  markAllAsRead: (type) => {
    return api.put('/api/notifications/read-all', type ? { type } : {});
  },

  /**
   * Delete a notification
   * @param {string} notificationId - Notification ID to delete
   * @returns {Promise} API response
   */
  deleteNotification: (notificationId) => {
    return api.del(`/api/notifications/${notificationId}`);
  },

  // ==================== USER PREFERENCES ENDPOINTS ====================

  /**
   * Get user notification preferences
   * @returns {Promise} API response with user preferences
   */
  getPreferences: () => {
    return api.get('/api/notifications/preferences');
  },

  /**
   * Update user notification preferences
   * @param {Object} preferences - Updated preference object
   * @returns {Promise} API response with updated preferences
   */
  updatePreferences: (preferences) => {
    return api.put('/api/notifications/preferences', { preferences });
  },

  // ==================== TEMPLATE MANAGEMENT ENDPOINTS ====================
  
  /**
   * Send test notification
   * @param {string} clubId - Club ID
   * @param {string} templateType - Template type (e.g., 'club_join_approved')
   * @param {Object} testData - Test data including email and sample data
   * @returns {Promise} API response with test result
   */
  sendTestNotification: (clubId, templateType, testData) => {
    return api.post(`/api/clubs/${clubId}/notification-templates/${templateType}/test`, testData);
  },

  /**
   * Get template preview
   * @param {string} clubId - Club ID
   * @param {string} templateType - Template type
   * @param {Object} previewData - Preview data including language and sample data
   * @returns {Promise} API response with preview content
   */
  getTemplatePreview: (clubId, templateType, previewData) => {
    return api.post(`/api/clubs/${clubId}/notification-templates/${templateType}/preview`, previewData);
  },

  /**
   * Get notification template
   * @param {string} clubId - Club ID
   * @param {string} templateType - Template type
   * @param {string} language - Language code (default: 'en')
   * @returns {Promise} API response with template data
   */
  getTemplate: (clubId, templateType, language = 'en') => {
    return api.get(`/api/clubs/${clubId}/notification-templates/${templateType}?language=${language}`);
  },

  /**
   * Update notification template
   * @param {string} clubId - Club ID
   * @param {string} templateType - Template type
   * @param {Object} templateData - Template data to update
   * @returns {Promise} API response with updated template
   */
  updateTemplate: (clubId, templateType, templateData) => {
    return api.put(`/api/clubs/${clubId}/notification-templates/${templateType}`, templateData);
  },

  /**
   * Get all notification templates for a club
   * @param {string} clubId - Club ID
   * @param {string} category - Optional category filter
   * @returns {Promise} API response with templates
   */
  getTemplates: (clubId, category = null) => {
    const params = category ? `?category=${category}` : '';
    return api.get(`/api/clubs/${clubId}/notification-templates${params}`);
  },

  /**
   * Get all available template categories
   * @returns {Promise} API response with categories
   */
  getTemplateCategories: () => {
    return api.get('/api/notification-templates/categories');
  },

  /**
   * Get all available notification types
   * @returns {Promise} API response with notification types
   */
  getNotificationTypes: () => {
    return api.get('/api/notification-templates/types');
  },

  /**
   * Reset template to system default
   * @param {string} clubId - Club ID
   * @param {string} type - Notification type
   * @returns {Promise} API response
   */
  resetTemplate: (clubId, type) => {
    return api.post(`/api/clubs/${clubId}/notification-templates/${type}/reset`);
  },

  // ==================== ADMIN/STATS ENDPOINTS ====================

  /**
   * Get notification statistics (admin only)
   * @param {Object} params - Query parameters
   * @returns {Promise} API response with comprehensive stats
   */
  getStats: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/api/admin/notifications/stats?${queryString}`);
  }
};

// ==================== NOTIFICATION CONSTANTS ====================

/**
 * Complete enumeration of all 54 notification types
 * Organized by category for easy reference
 */
export const NOTIFICATION_TYPES = {
  // Club Management (8 types)
  CLUB_JOIN_REQUEST: 'club_join_request',
  CLUB_JOIN_APPROVED: 'club_join_approved',
  CLUB_JOIN_REJECTED: 'club_join_rejected',
  CLUB_PAYMENT_DUE: 'club_payment_due',
  CLUB_PAYMENT_RECEIVED: 'club_payment_received',
  CLUB_ROLE_ASSIGNED: 'club_role_assigned',
  CLUB_WELCOME: 'club_welcome',
  CLUB_MEMBER_REMOVED: 'club_member_removed',

  // Program Registration (11 types)
  PROGRAM_REGISTRATION_SUBMITTED: 'program_registration_submitted',
  PROGRAM_REGISTRATION_APPROVED: 'program_registration_approved',
  PROGRAM_REGISTRATION_REJECTED: 'program_registration_rejected',
  PROGRAM_REGISTRATION_WAITLISTED: 'program_registration_waitlisted',
  PROGRAM_REGISTRATION_DEADLINE: 'program_registration_deadline',
  PROGRAM_CAPACITY_REACHED: 'program_capacity_reached',
  PROGRAM_DOCUMENT_REQUIRED: 'program_document_required',
  PROGRAM_PAYMENT_DUE: 'program_payment_due',
  PROGRAM_WAITLIST_POSITION_CHANGED: 'program_waitlist_position_changed',
  PROGRAM_STATUS_CHANGED: 'program_status_changed',
  PROGRAM_ROLE_ASSIGNED: 'program_role_assigned',

  // Event Management (8 types)
  EVENT_CREATED: 'event_created',
  EVENT_UPDATED: 'event_updated',
  EVENT_CANCELLED: 'event_cancelled',
  EVENT_REMINDER: 'event_reminder',
  EVENT_RSVP_DEADLINE: 'event_rsvp_deadline',
  EVENT_WEATHER_ALERT: 'event_weather_alert',
  EVENT_RESULTS_POSTED: 'event_results_posted',
  EVENT_CAPACITY_REACHED: 'event_capacity_reached',

  // RSVP Management (5 types)
  RSVP_RECEIVED: 'rsvp_received',
  RSVP_CHANGED: 'rsvp_changed',
  RSVP_REMINDER: 'rsvp_reminder',
  RSVP_ATTENDANCE_CONFIRMED: 'rsvp_attendance_confirmed',
  RSVP_DEADLINE_PASSED: 'rsvp_deadline_passed',

  // Equipment Management (6 types)
  EQUIPMENT_ASSIGNED: 'equipment_assigned',
  EQUIPMENT_RETURN_DUE: 'equipment_return_due',
  EQUIPMENT_OVERDUE: 'equipment_overdue',
  EQUIPMENT_RETURNED: 'equipment_returned',
  EQUIPMENT_MAINTENANCE_REQUIRED: 'equipment_maintenance_required',
  EQUIPMENT_DAMAGED_REPORTED: 'equipment_damaged_reported',

  // Team Management (10 types)
  TEAM_ROSTER_ADDED: 'team_roster_added',
  TEAM_ROSTER_REMOVED: 'team_roster_removed',
  TEAM_ROLE_CHANGED: 'team_role_changed',
  TEAM_ANNOUNCEMENT: 'team_announcement',
  TEAM_PRACTICE_SCHEDULED: 'team_practice_scheduled',
  TEAM_PRACTICE_CANCELLED: 'team_practice_cancelled',
  TEAM_INVITATION_SENT: 'team_invitation_sent',
  TEAM_INVITATION_ACCEPTED: 'team_invitation_accepted',
  TEAM_INVITATION_DECLINED: 'team_invitation_declined',
  TEAM_INVITATION_EXPIRED: 'team_invitation_expired',

  // Assignment Management (5 types)
  ASSIGNMENT_CREATED: 'assignment_created',
  ASSIGNMENT_DEADLINE_REMINDER: 'assignment_deadline_reminder',
  ASSIGNMENT_OVERDUE: 'assignment_overdue',
  ASSIGNMENT_COMPLETED: 'assignment_completed',
  ASSIGNMENT_UPDATED: 'assignment_updated',

  // System Notifications (6 types)
  PROFILE_UPDATE_REQUIRED: 'profile_update_required',
  SECURITY_ALERT: 'security_alert',
  SYSTEM_MAINTENANCE: 'system_maintenance',
  POLICY_UPDATE: 'policy_update',
  ACCOUNT_SUSPENDED: 'account_suspended',
  PASSWORD_RESET_REQUEST: 'password_reset_request'
};

/**
 * Notification categories for filtering and organization
 */
export const NOTIFICATION_CATEGORIES = {
  CLUB: 'club',
  PROGRAM: 'program',
  EVENT: 'event',
  RSVP: 'rsvp',
  EQUIPMENT: 'equipment',
  TEAM: 'team',
  ASSIGNMENT: 'assignment',
  SYSTEM: 'system'
};

/**
 * Priority levels for notifications
 */
export const NOTIFICATION_PRIORITIES = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent'
};

/**
 * Delivery channels
 */
export const NOTIFICATION_CHANNELS = {
  IN_APP: 'in_app',
  EMAIL: 'email',
  SMS: 'sms' // Future implementation
};

/**
 * Category metadata with display information
 */
export const CATEGORY_METADATA = {
  [NOTIFICATION_CATEGORIES.CLUB]: {
    name: 'Club Management',
    icon: '🏆',
    description: 'Club membership, roles, and administrative notifications',
    types: [
      NOTIFICATION_TYPES.CLUB_JOIN_REQUEST,
      NOTIFICATION_TYPES.CLUB_JOIN_APPROVED,
      NOTIFICATION_TYPES.CLUB_JOIN_REJECTED,
      NOTIFICATION_TYPES.CLUB_PAYMENT_DUE,
      NOTIFICATION_TYPES.CLUB_PAYMENT_RECEIVED,
      NOTIFICATION_TYPES.CLUB_ROLE_ASSIGNED,
      NOTIFICATION_TYPES.CLUB_WELCOME,
      NOTIFICATION_TYPES.CLUB_MEMBER_REMOVED
    ]
  },
  [NOTIFICATION_CATEGORIES.PROGRAM]: {
    name: 'Program Registration',
    icon: '📋',
    description: 'Program applications, approvals, and registration updates',
    types: [
      NOTIFICATION_TYPES.PROGRAM_REGISTRATION_SUBMITTED,
      NOTIFICATION_TYPES.PROGRAM_REGISTRATION_APPROVED,
      NOTIFICATION_TYPES.PROGRAM_REGISTRATION_REJECTED,
      NOTIFICATION_TYPES.PROGRAM_REGISTRATION_WAITLISTED,
      NOTIFICATION_TYPES.PROGRAM_REGISTRATION_DEADLINE,
      NOTIFICATION_TYPES.PROGRAM_CAPACITY_REACHED,
      NOTIFICATION_TYPES.PROGRAM_DOCUMENT_REQUIRED,
      NOTIFICATION_TYPES.PROGRAM_PAYMENT_DUE,
      NOTIFICATION_TYPES.PROGRAM_WAITLIST_POSITION_CHANGED,
      NOTIFICATION_TYPES.PROGRAM_STATUS_CHANGED,
      NOTIFICATION_TYPES.PROGRAM_ROLE_ASSIGNED
    ]
  },
  [NOTIFICATION_CATEGORIES.EVENT]: {
    name: 'Events',
    icon: '📅',
    description: 'Event schedules, updates, reminders, and results',
    types: [
      NOTIFICATION_TYPES.EVENT_CREATED,
      NOTIFICATION_TYPES.EVENT_UPDATED,
      NOTIFICATION_TYPES.EVENT_CANCELLED,
      NOTIFICATION_TYPES.EVENT_REMINDER,
      NOTIFICATION_TYPES.EVENT_RSVP_DEADLINE,
      NOTIFICATION_TYPES.EVENT_WEATHER_ALERT,
      NOTIFICATION_TYPES.EVENT_RESULTS_POSTED,
      NOTIFICATION_TYPES.EVENT_CAPACITY_REACHED
    ]
  },
  [NOTIFICATION_CATEGORIES.RSVP]: {
    name: 'RSVP Management',
    icon: '✋',
    description: 'RSVP responses, reminders, and attendance tracking',
    types: [
      NOTIFICATION_TYPES.RSVP_RECEIVED,
      NOTIFICATION_TYPES.RSVP_CHANGED,
      NOTIFICATION_TYPES.RSVP_REMINDER,
      NOTIFICATION_TYPES.RSVP_ATTENDANCE_CONFIRMED,
      NOTIFICATION_TYPES.RSVP_DEADLINE_PASSED
    ]
  },
  [NOTIFICATION_CATEGORIES.EQUIPMENT]: {
    name: 'Equipment',
    icon: '⚽',
    description: 'Equipment assignments, returns, and maintenance',
    types: [
      NOTIFICATION_TYPES.EQUIPMENT_ASSIGNED,
      NOTIFICATION_TYPES.EQUIPMENT_RETURN_DUE,
      NOTIFICATION_TYPES.EQUIPMENT_OVERDUE,
      NOTIFICATION_TYPES.EQUIPMENT_RETURNED,
      NOTIFICATION_TYPES.EQUIPMENT_MAINTENANCE_REQUIRED,
      NOTIFICATION_TYPES.EQUIPMENT_DAMAGED_REPORTED
    ]
  },
  [NOTIFICATION_CATEGORIES.TEAM]: {
    name: 'Team Management',
    icon: '👥',
    description: 'Team roster, roles, announcements, practice schedules, and invitations',
    types: [
      NOTIFICATION_TYPES.TEAM_ROSTER_ADDED,
      NOTIFICATION_TYPES.TEAM_ROSTER_REMOVED,
      NOTIFICATION_TYPES.TEAM_ROLE_CHANGED,
      NOTIFICATION_TYPES.TEAM_ANNOUNCEMENT,
      NOTIFICATION_TYPES.TEAM_PRACTICE_SCHEDULED,
      NOTIFICATION_TYPES.TEAM_PRACTICE_CANCELLED,
      NOTIFICATION_TYPES.TEAM_INVITATION_SENT,
      NOTIFICATION_TYPES.TEAM_INVITATION_ACCEPTED,
      NOTIFICATION_TYPES.TEAM_INVITATION_DECLINED,
      NOTIFICATION_TYPES.TEAM_INVITATION_EXPIRED
    ]
  },
  [NOTIFICATION_CATEGORIES.ASSIGNMENT]: {
    name: 'Assignments',
    icon: '📝',
    description: 'Tasks, assignments, deadlines, and completion tracking',
    types: [
      NOTIFICATION_TYPES.ASSIGNMENT_CREATED,
      NOTIFICATION_TYPES.ASSIGNMENT_DEADLINE_REMINDER,
      NOTIFICATION_TYPES.ASSIGNMENT_OVERDUE,
      NOTIFICATION_TYPES.ASSIGNMENT_COMPLETED,
      NOTIFICATION_TYPES.ASSIGNMENT_UPDATED
    ]
  },
  [NOTIFICATION_CATEGORIES.SYSTEM]: {
    name: 'System',
    icon: '⚙️',
    description: 'System updates, security alerts, and account notifications',
    types: [
      NOTIFICATION_TYPES.PROFILE_UPDATE_REQUIRED,
      NOTIFICATION_TYPES.SECURITY_ALERT,
      NOTIFICATION_TYPES.SYSTEM_MAINTENANCE,
      NOTIFICATION_TYPES.POLICY_UPDATE,
      NOTIFICATION_TYPES.ACCOUNT_SUSPENDED,
      NOTIFICATION_TYPES.PASSWORD_RESET_REQUEST
    ]
  }
};

/**
 * Priority metadata with styling information
 */
export const PRIORITY_METADATA = {
  [NOTIFICATION_PRIORITIES.LOW]: {
    name: 'Low',
    color: '#9E9E9E',
    backgroundColor: '#F5F5F5',
    icon: '🔵'
  },
  [NOTIFICATION_PRIORITIES.MEDIUM]: {
    name: 'Medium',
    color: '#4CAF50',
    backgroundColor: '#E8F5E8',
    icon: '🟢'
  },
  [NOTIFICATION_PRIORITIES.HIGH]: {
    name: 'High',
    color: '#FF8800',
    backgroundColor: '#FFF3E0',
    icon: '🟠'
  },
  [NOTIFICATION_PRIORITIES.URGENT]: {
    name: 'Urgent',
    color: '#FF4444',
    backgroundColor: '#FFEBEE',
    icon: '🔴'
  }
};

/**
 * Utility functions for notification handling
 */
export const notificationUtils = {
  /**
   * Get category metadata for a notification type
   * @param {string} type - Notification type
   * @returns {Object} Category metadata
   */
  getCategoryForType: (type) => {
    for (const [categoryKey, metadata] of Object.entries(CATEGORY_METADATA)) {
      if (metadata.types.includes(type)) {
        return { key: categoryKey, ...metadata };
      }
    }
    return { key: 'system', ...CATEGORY_METADATA.system };
  },

  /**
   * Get priority styling information
   * @param {string} priority - Priority level
   * @returns {Object} Priority styling metadata
   */
  getPriorityMetadata: (priority) => {
    return PRIORITY_METADATA[priority] || PRIORITY_METADATA[NOTIFICATION_PRIORITIES.MEDIUM];
  },

  /**
   * Format time ago string
   * @param {Date|string} date - Date to format
   * @returns {string} Formatted time ago string
   */
  getTimeAgo: (date) => {
    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);

    if (months > 0) return `${months}mo ago`;
    if (weeks > 0) return `${weeks}w ago`;
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  },

  /**
   * Check if notification is recent (within last hour)
   * @param {Date|string} date - Notification date
   * @returns {boolean} True if recent
   */
  isRecent: (date) => {
    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    return diff < 3600000; // 1 hour in milliseconds
  },

  /**
   * Get action URL for notification
   * @param {Object} notification - Notification object
   * @returns {string} Action URL
   */
  getActionUrl: (notification) => {
    return notification.data?.actionUrl || notification.actionUrl || '#';
  },

  /**
   * Get action label for notification
   * @param {Object} notification - Notification object
   * @returns {string} Action label
   */
  getActionLabel: (notification) => {
    return notification.data?.actionLabel || notification.actionLabel || 'View Details';
  },

  /**
   * Check if notification has action
   * @param {Object} notification - Notification object
   * @returns {boolean} True if has action
   */
  hasAction: (notification) => {
    return !!(notification.data?.actionUrl || notification.actionUrl);
  }
};

export default notificationApi;
